"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { BrandLogo } from "@/components/brand-logo";
import { IconRenderer } from "@/components/ui/icon-renderer";
import { Button } from "@/components/ui/button";
import { useNavigation } from "@/components/navigation-provider";
import type { NavigationData } from "@/lib/api";
import { isEmojiIcon } from "@/lib/icons";
import { LOGIN_DASHBOARD_URL, SIGNUP_DASHBOARD_URL } from "@/lib/auth-urls";
import { cn } from "@/lib/utils";

type ManagedHeaderItem = NonNullable<NavigationData["headerMenu"]>[number];
type ManagedHeaderChild = ManagedHeaderItem["children"][number];
type MenuDevice = "desktop" | "mobile";

const GlobalSearch = dynamic(
  () => import("@/components/global-search").then((mod) => mod.GlobalSearch),
  { ssr: false }
);

const ThemeToggle = dynamic(
  () => import("@/components/theme-toggle").then((mod) => mod.ThemeToggle),
  { ssr: false }
);

type MenuTone = {
  iconKey: string;
  panelGlowClass: string;
  iconWrapClass: string;
  accentTextClass: string;
  softSurfaceClass: string;
  hoverBorderClass: string;
  mobileIconWrapClass: string;
};

const MENU_TONES: Record<string, MenuTone> = {
  general: {
    iconKey: "Sparkles",
    panelGlowClass: "shadow-sky-500/20",
    iconWrapClass: "bg-gradient-to-br from-sky-500 to-cyan-500 text-white",
    accentTextClass: "group-hover:text-sky-600 dark:group-hover:text-sky-300",
    softSurfaceClass: "bg-slate-50/80 hover:bg-slate-100 dark:bg-slate-900/70 dark:hover:bg-slate-800/90",
    hoverBorderClass: "hover:border-sky-200 dark:hover:border-sky-800",
    mobileIconWrapClass: "bg-gradient-to-br from-sky-500 to-cyan-500 text-white",
  },
  services: {
    iconKey: "Code",
    panelGlowClass: "shadow-blue-500/20",
    iconWrapClass: "bg-gradient-to-br from-blue-600 to-cyan-500 text-white",
    accentTextClass: "group-hover:text-blue-600 dark:group-hover:text-blue-300",
    softSurfaceClass: "bg-slate-50/80 hover:bg-slate-100 dark:bg-slate-900/70 dark:hover:bg-slate-800/90",
    hoverBorderClass: "hover:border-blue-200 dark:hover:border-blue-800",
    mobileIconWrapClass: "bg-gradient-to-br from-blue-600 to-cyan-500 text-white",
  },
  products: {
    iconKey: "ShoppingCart",
    panelGlowClass: "shadow-violet-500/20",
    iconWrapClass: "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white",
    accentTextClass: "group-hover:text-violet-600 dark:group-hover:text-violet-300",
    softSurfaceClass: "bg-slate-50/80 hover:bg-slate-100 dark:bg-slate-900/70 dark:hover:bg-slate-800/90",
    hoverBorderClass: "hover:border-violet-200 dark:hover:border-violet-800",
    mobileIconWrapClass: "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white",
  },
  industries: {
    iconKey: "Building2",
    panelGlowClass: "shadow-emerald-500/20",
    iconWrapClass: "bg-gradient-to-br from-emerald-500 to-teal-500 text-white",
    accentTextClass: "group-hover:text-emerald-600 dark:group-hover:text-emerald-300",
    softSurfaceClass: "bg-slate-50/80 hover:bg-slate-100 dark:bg-slate-900/70 dark:hover:bg-slate-800/90",
    hoverBorderClass: "hover:border-emerald-200 dark:hover:border-emerald-800",
    mobileIconWrapClass: "bg-gradient-to-br from-emerald-500 to-teal-500 text-white",
  },
  resources: {
    iconKey: "BookOpen",
    panelGlowClass: "shadow-amber-500/20",
    iconWrapClass: "bg-gradient-to-br from-amber-500 to-orange-500 text-white",
    accentTextClass: "group-hover:text-amber-600 dark:group-hover:text-amber-300",
    softSurfaceClass: "bg-slate-50/80 hover:bg-slate-100 dark:bg-slate-900/70 dark:hover:bg-slate-800/90",
    hoverBorderClass: "hover:border-amber-200 dark:hover:border-amber-800",
    mobileIconWrapClass: "bg-gradient-to-br from-amber-500 to-orange-500 text-white",
  },
  company: {
    iconKey: "Users",
    panelGlowClass: "shadow-rose-500/20",
    iconWrapClass: "bg-gradient-to-br from-rose-500 to-pink-500 text-white",
    accentTextClass: "group-hover:text-rose-600 dark:group-hover:text-rose-300",
    softSurfaceClass: "bg-slate-50/80 hover:bg-slate-100 dark:bg-slate-900/70 dark:hover:bg-slate-800/90",
    hoverBorderClass: "hover:border-rose-200 dark:hover:border-rose-800",
    mobileIconWrapClass: "bg-gradient-to-br from-rose-500 to-pink-500 text-white",
  },
  default: {
    iconKey: "Zap",
    panelGlowClass: "shadow-slate-500/20",
    iconWrapClass: "bg-gradient-to-br from-slate-500 to-slate-700 text-white",
    accentTextClass: "group-hover:text-slate-700 dark:group-hover:text-slate-200",
    softSurfaceClass: "bg-slate-50/80 hover:bg-slate-100 dark:bg-slate-900/70 dark:hover:bg-slate-800/90",
    hoverBorderClass: "hover:border-slate-200 dark:hover:border-slate-700",
    mobileIconWrapClass: "bg-gradient-to-br from-slate-500 to-slate-700 text-white",
  },
};

function getMenuTone(layoutKey?: string | null): MenuTone {
  if (!layoutKey) {
    return MENU_TONES.default;
  }

  return MENU_TONES[layoutKey] || MENU_TONES.default;
}

function inferMenuToneKey(item: { title: string; url: string; cssClass?: string | null }): string {
  if (item.cssClass && MENU_TONES[item.cssClass]) {
    return item.cssClass;
  }

  const value = `${item.title} ${item.url}`.toLowerCase();

  if (value.includes("service")) return "services";
  if (value.includes("product") || value.includes("pricing") || value.includes("feature") || value.includes("solution")) return "products";
  if (value.includes("industry")) return "industries";
  if (value.includes("resource") || value.includes("insight") || value.includes("blog") || value.includes("case-stud")) return "resources";
  if (value.includes("company") || value.includes("about") || value.includes("team") || value.includes("career") || value.includes("contact")) return "company";

  return "general";
}

function isHomeMenuItem(item: { title: string; url: string }) {
  const normalizedTitle = item.title.trim().toLowerCase();
  const normalizedUrl = item.url.trim().toLowerCase().replace(/\/+$/, "") || "/";
  return normalizedTitle === "home" || normalizedUrl === "/" || normalizedUrl === "/home";
}

function normalizeMenuUrl(url: string) {
  const normalized = url.trim();
  const withoutTrailingSlash = normalized.replace(/\/+$/, "") || "/";
  if (withoutTrailingSlash === "/case-studies") return "/resources/case-studies";
  return normalized;
}

function restructureHeaderMenu(items: ManagedHeaderItem[]): ManagedHeaderItem[] {
  const normalized = items
    .filter((item) => !isHomeMenuItem(item))
    .map((item) => ({
      ...item,
      url: normalizeMenuUrl(item.url),
      cssClass: inferMenuToneKey(item),
      children: (item.children || []).map((child) => ({
        ...child,
        url: normalizeMenuUrl(child.url),
      })),
    }));

  const resourcesItem = normalized.find((item) => {
    const label = item.title.trim().toLowerCase();
    return label === "resources" || item.cssClass === "resources";
  });

  if (resourcesItem) {
    const hasChangelog = (resourcesItem.children || []).some((child) => {
      const childTitle = child.title.trim().toLowerCase();
      const childUrl = normalizeMenuUrl(child.url).toLowerCase().replace(/\/+$/, "") || "/";
      return childTitle === "changelog" || childUrl === "/changelog" || childUrl === "/resources/changelog";
    });

    if (!hasChangelog) {
      const changelogChild = {
        id: `${resourcesItem.id}-changelog`,
        parentId: resourcesItem.id,
        title: "Changelog",
        url: "/changelog",
        description: "Track new features, improvements, fixes, and security updates.",
        iconKey: "Milestone",
        target: null,
        cssClass: "resources",
        columnLabel: null,
        showOnDesktop: true,
        showOnMobile: true,
        children: [],
      } as ManagedHeaderChild;

      resourcesItem.children = [changelogChild, ...(resourcesItem.children || [])];
    }
  }

  const integrationsItem = normalized.find((item) => {
    const label = item.title.trim().toLowerCase();
    const url = item.url.trim().toLowerCase();
    return label.includes("integration") || url.includes("integration");
  });

  if (!resourcesItem || !integrationsItem || resourcesItem === integrationsItem) {
    return normalized;
  }

  const integrationChild = {
    ...integrationsItem,
    id: `${integrationsItem.id}-nested`,
    parentId: resourcesItem.id,
    cssClass: "resources",
  } as ManagedHeaderChild;

  const mergedResources: ManagedHeaderItem = {
    ...resourcesItem,
    children: [...(resourcesItem.children || []), integrationChild],
  };

  return normalized
    .filter((item) => item !== integrationsItem)
    .map((item) => (item === resourcesItem ? mergedResources : item));
}

function inferMenuItemIcon(item: { title: string; url: string; iconKey?: string | null }, layoutKey?: string | null) {
  if (item.iconKey) {
    return item.iconKey;
  }

  const value = `${item.title} ${item.url}`.toLowerCase();

  if (value.includes("integration") || value.includes("connect") || value.includes("api")) return "Plug";
  if (value.includes("pricing") || value.includes("plan") || value.includes("billing")) return "BadgeDollarSign";
  if (value.includes("feature") || value.includes("capabilit")) return "Sparkles";
  if (value.includes("solution")) return "Lightbulb";
  if (value.includes("use-case") || value.includes("use case")) return "Target";
  if (value.includes("about")) return "Info";
  if (value.includes("team")) return "Users";
  if (value.includes("career") || value.includes("job")) return "Briefcase";
  if (value.includes("contact") || value.includes("support")) return "MessageCircle";
  if (value.includes("privacy") || value.includes("policy") || value.includes("compliance")) return "ShieldCheck";
  if (value.includes("terms") || value.includes("legal")) return "Scale";
  if (value.includes("ai") || value.includes("artificial-intelligence")) return "Brain";
  if (value.includes("cloud") || value.includes("devops") || value.includes("aws") || value.includes("azure")) return "Cloud";
  if (value.includes("mobile") || value.includes("app")) return "Smartphone";
  if (value.includes("web") || value.includes("website")) return "Globe";
  if (value.includes("qa") || value.includes("testing")) return "ShieldCheck";
  if (value.includes("case-study")) return "CheckSquare";
  if (value.includes("client") || value.includes("portfolio")) return "Briefcase";
  if (value.includes("resource") || value.includes("insight") || value.includes("blog")) return "BookOpen";
  if (value.includes("product")) return "ShoppingCart";
  if (value.includes("industry")) return "Building2";
  if (value.includes("career") || value.includes("job") || value.includes("team") || value.includes("company")) return "Users";
  if (value.includes("contact") || value.includes("consultation")) return "Rocket";

  return getMenuTone(layoutKey).iconKey;
}

function MenuTypeIcon({
  item,
  layoutKey,
  className,
  emojiClassName,
}: {
  item: { title: string; url: string; iconKey?: string | null };
  layoutKey?: string | null;
  className?: string;
  emojiClassName?: string;
}) {
  return <IconRenderer iconKey={inferMenuItemIcon(item, layoutKey)} className={className} emojiClassName={emojiClassName} />;
}

function isTrendingHighlight(title: string) {
  return /^trending\b/i.test(title) || /trending technologies/i.test(title) || /trending services/i.test(title);
}

function getMenuItemKey(item: { id?: string; title: string; url: string }, index: number, prefix: string) {
  return item.id || `${prefix}-${item.title}-${item.url}-${index}`;
}

function getDropdownPanelClass(layoutKey?: string | null) {
  switch (layoutKey) {
    case "general":
      return "w-[min(calc(100vw-2rem),52rem)]";
    case "services":
      return "w-[min(calc(100vw-2rem),72rem)]";
    case "products":
      return "w-[min(calc(100vw-2rem),78rem)]";
    case "industries":
      return "w-[min(calc(100vw-2rem),44rem)]";
    case "resources":
      return "w-[min(calc(100vw-2rem),46rem)]";
    case "company":
      return "w-[min(calc(100vw-2rem),22rem)]";
    default:
      return "w-[min(calc(100vw-2rem),28rem)]";
  }
}

function isFeatureMenu(item: ManagedHeaderItem) {
  const title = item.title.trim().toLowerCase();
  const url = item.url.trim().toLowerCase();
  return title === "features" || url === "/features";
}

function getProductsItemIconStyles(index: number) {
  const palettes = [
    {
      wrap: "bg-gradient-to-br from-sky-500 to-cyan-500 text-white",
      glow: "shadow-sky-500/25",
      arrow: "group-hover:text-sky-500",
    },
    {
      wrap: "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white",
      glow: "shadow-violet-500/25",
      arrow: "group-hover:text-violet-500",
    },
    {
      wrap: "bg-gradient-to-br from-emerald-500 to-teal-500 text-white",
      glow: "shadow-emerald-500/25",
      arrow: "group-hover:text-emerald-500",
    },
    {
      wrap: "bg-gradient-to-br from-amber-500 to-orange-500 text-white",
      glow: "shadow-amber-500/25",
      arrow: "group-hover:text-amber-500",
    },
    {
      wrap: "bg-gradient-to-br from-rose-500 to-pink-500 text-white",
      glow: "shadow-rose-500/25",
      arrow: "group-hover:text-rose-500",
    },
    {
      wrap: "bg-gradient-to-br from-indigo-500 to-blue-600 text-white",
      glow: "shadow-indigo-500/25",
      arrow: "group-hover:text-indigo-500",
    },
  ];

  return palettes[index % palettes.length];
}

function getDropdownPositionClass(layoutKey?: string | null, scrolled?: boolean) {
  const topOffsetClass = scrolled ? "top-[4.75rem]" : "top-[5.75rem]";

  switch (layoutKey) {
    case "resources":
    case "company":
      return "absolute left-1/2 top-full z-50 pt-4 -translate-x-1/2";
    default:
      return cn("fixed left-1/2 z-50 -translate-x-1/2", topOffsetClass);
  }
}

function isVisibleOnDevice(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "false" || normalized === "0") return false;
    if (normalized === "true" || normalized === "1") return true;
  }

  return true;
}

function MenuLink({ href, target, onClick, children, className }: { href: string; target?: string | null; onClick?: () => void; children: React.ReactNode; className?: string }) {
  return (
    <Link href={href} target={target || undefined} onClick={onClick} className={className}>
      {children}
    </Link>
  );
}

function filterMenuItemsForDevice(items: ManagedHeaderItem[], device: MenuDevice): ManagedHeaderItem[] {
  return items
    .filter((item) => (device === "desktop" ? isVisibleOnDevice(item.showOnDesktop) : isVisibleOnDevice(item.showOnMobile)))
    .map((item) => ({
      ...item,
      children: filterMenuItemsForDevice(item.children as ManagedHeaderItem[], device) as ManagedHeaderChild[],
    }));
}

function GeneralMenu({ item, closeMenus }: { item: ManagedHeaderItem; closeMenus: () => void }) {
  const tone = getMenuTone(item.cssClass);

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {item.children.map((child, index) => {
        return (
          <MenuLink
            key={getMenuItemKey(child, index, "general")}
            href={child.url}
            target={child.target}
            onClick={closeMenus}
            className={cn(
              "group flex items-start gap-3 rounded-2xl border border-transparent p-4 transition hover:-translate-y-0.5 hover:shadow-lg",
              tone.softSurfaceClass,
              tone.hoverBorderClass
            )}
          >
            <div className={cn("flex h-11 w-11 items-center justify-center rounded-2xl shadow-lg", tone.iconWrapClass, tone.panelGlowClass)}>
              <MenuTypeIcon item={child} layoutKey={item.cssClass} className="h-5 w-5" emojiClassName="text-xl leading-none" />
            </div>
            <div className="min-w-0 flex-1">
              <div className={cn("text-sm font-semibold text-slate-900 transition dark:text-white", tone.accentTextClass)}>
                {child.title}
              </div>
            </div>
          </MenuLink>
        );
      })}
    </div>
  );
}

function ServicesMenu({ item, closeMenus }: { item: ManagedHeaderItem; closeMenus: () => void }) {
  const tone = getMenuTone(item.cssClass);

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {item.children.map((child, childIndex) => {
        return (
          <div key={getMenuItemKey(child, childIndex, "services") } className="space-y-2.5 min-w-0">
            <MenuLink
              href={child.url}
              target={child.target}
              onClick={closeMenus}
              className={cn("flex items-center gap-2.5 rounded-2xl px-3 py-2.5 transition hover:shadow-md", tone.softSurfaceClass)}
            >
              <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", tone.iconWrapClass)}>
                <MenuTypeIcon item={child} layoutKey={item.cssClass} className="h-4 w-4" emojiClassName="text-lg leading-none" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold leading-5 text-slate-900 dark:text-white">{child.columnLabel || child.title}</div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">Solutions & services</div>
              </div>
            </MenuLink>
            <div className="space-y-0.5">
              {child.children.map((grandChild, grandChildIndex) => (
                <MenuLink
                  key={getMenuItemKey(grandChild, grandChildIndex, `${child.title}-services-child`)}
                  href={grandChild.url}
                  target={grandChild.target}
                  onClick={closeMenus}
                  className={cn("block rounded-xl px-2.5 py-1.5 text-[13px] leading-5 text-slate-600 transition dark:text-slate-300 dark:hover:bg-slate-800", item.cssClass === "services" ? "hover:bg-sky-50 hover:text-sky-600 dark:hover:text-sky-300" : "hover:bg-slate-100 hover:text-slate-900")}
                >
                  {grandChild.title}
                </MenuLink>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProductMenu({ item, closeMenus }: { item: ManagedHeaderItem; closeMenus: () => void }) {
  const tone = getMenuTone(item.cssClass);
  const compactFeatureMenu = isFeatureMenu(item) && item.children.length >= 12;

  return (
    <div className={cn("grid md:grid-cols-2 lg:grid-cols-4", compactFeatureMenu ? "gap-2" : "gap-3")}>
      {item.children.map((child, index) => {
        const iconStyle = getProductsItemIconStyles(index);

        return (
          <MenuLink
            key={getMenuItemKey(child, index, "products")}
            href={child.url}
            target={child.target}
            onClick={closeMenus}
            className={cn(
              "group h-full rounded-2xl border border-transparent transition hover:-translate-y-0.5 hover:shadow-lg",
              compactFeatureMenu ? "p-2.5" : "p-4",
              tone.softSurfaceClass,
              tone.hoverBorderClass
            )}
          >
            <div className={cn("flex h-full items-start", compactFeatureMenu ? "gap-2" : "gap-3")}>
              <div className={cn(
                "flex shrink-0 items-center justify-center shadow-lg",
                compactFeatureMenu ? "h-8 w-8 rounded-xl" : "h-11 w-11 rounded-2xl",
                iconStyle.wrap,
                iconStyle.glow
              )}>
                <MenuTypeIcon item={child} layoutKey={item.cssClass} className={compactFeatureMenu ? "h-4 w-4" : "h-5 w-5"} emojiClassName={compactFeatureMenu ? "text-base leading-none" : "text-xl leading-none"} />
              </div>
              <div className={cn("flex min-w-0 flex-1 items-start justify-between", compactFeatureMenu ? "gap-2" : "gap-3")}>
                <div className="min-w-0 flex-1">
                  <div className={cn(compactFeatureMenu ? "text-[13px] leading-5" : "text-sm", "font-semibold text-slate-900 transition dark:text-white", tone.accentTextClass)}>
                    {child.title}
                  </div>
                  {!compactFeatureMenu ? (
                    <div className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">{child.description || "Enterprise-ready product suite"}</div>
                  ) : null}
                </div>
                <ArrowRight className={cn("mt-0.5 shrink-0 text-slate-400 transition", iconStyle.arrow, compactFeatureMenu ? "h-3.5 w-3.5" : "h-4 w-4")} />
              </div>
            </div>
          </MenuLink>
        );
      })}
    </div>
  );
}

function IndustriesMenu({ item, closeMenus }: { item: ManagedHeaderItem; closeMenus: () => void }) {
  const tone = getMenuTone(item.cssClass);

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {item.children.map((child, index) => {
        const emojiIcon = isEmojiIcon(child.iconKey);
        return (
          <MenuLink
            key={getMenuItemKey(child, index, "industries")}
            href={child.url}
            target={child.target}
            onClick={closeMenus}
            className={cn("group flex items-start gap-3 rounded-2xl border border-transparent p-4 transition hover:-translate-y-0.5 hover:shadow-lg", tone.softSurfaceClass, tone.hoverBorderClass)}
          >
            <div className={cn(
              "flex h-11 w-11 items-center justify-center rounded-2xl",
              emojiIcon
                ? "border border-emerald-200/70 bg-emerald-50 text-emerald-700 shadow-sm dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                : cn(tone.iconWrapClass, "shadow-lg", tone.panelGlowClass)
            )}>
              <MenuTypeIcon item={child} layoutKey={item.cssClass} className="h-5 w-5" emojiClassName="text-2xl leading-none" />
            </div>
            <div>
              <div className={cn("text-sm font-semibold text-slate-900 transition dark:text-white", tone.accentTextClass)}>
                {child.title}
              </div>
              <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{child.description || child.title}</div>
            </div>
          </MenuLink>
        );
      })}
    </div>
  );
}

function ResourcesMenu({ item, closeMenus }: { item: ManagedHeaderItem; closeMenus: () => void }) {
  const tone = getMenuTone(item.cssClass);

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {item.children.map((child, index) => {
        return (
          <MenuLink
            key={getMenuItemKey(child, index, "resources")}
            href={child.url}
            target={child.target}
            onClick={closeMenus}
            className={cn("group flex items-start gap-3 rounded-2xl border border-transparent p-4 transition hover:-translate-y-0.5 hover:shadow-lg", tone.softSurfaceClass, tone.hoverBorderClass)}
          >
            <div className={cn("flex h-11 w-11 items-center justify-center rounded-2xl shadow-lg", tone.iconWrapClass, tone.panelGlowClass)}>
              <MenuTypeIcon item={child} layoutKey={item.cssClass} className="h-5 w-5" emojiClassName="text-xl leading-none" />
            </div>
            <div>
              <div className={cn("text-sm font-semibold text-slate-900 transition dark:text-white", tone.accentTextClass)}>
                {child.title}
              </div>
              <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{child.description || child.title}</div>
            </div>
          </MenuLink>
        );
      })}
    </div>
  );
}

function CompanyMenu({ item, closeMenus }: { item: ManagedHeaderItem; closeMenus: () => void }) {
  const tone = getMenuTone(item.cssClass);

  return (
    <div className="grid gap-2">
      {item.children.map((child, index) => (
        <MenuLink
          key={getMenuItemKey(child, index, "company")}
          href={child.url}
          target={child.target}
          onClick={closeMenus}
          className={cn("group flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-slate-600 transition dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white", tone.softSurfaceClass)}
        >
          <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", tone.iconWrapClass)}>
            <MenuTypeIcon item={child} layoutKey={item.cssClass} className="h-4 w-4" emojiClassName="text-lg leading-none" />
          </div>
          <span className={cn("transition", tone.accentTextClass)}>{child.title}</span>
        </MenuLink>
      ))}
    </div>
  );
}

function DropdownContent({ item, closeMenus }: { item: ManagedHeaderItem; closeMenus: () => void }) {
  switch (item.cssClass) {
    case "general":
      return <GeneralMenu item={item} closeMenus={closeMenus} />;
    case "services":
      return <ServicesMenu item={item} closeMenus={closeMenus} />;
    case "products":
      return <ProductMenu item={item} closeMenus={closeMenus} />;
    case "industries":
      return <IndustriesMenu item={item} closeMenus={closeMenus} />;
    case "resources":
      return <ResourcesMenu item={item} closeMenus={closeMenus} />;
    case "company":
      return <CompanyMenu item={item} closeMenus={closeMenus} />;
    default:
      return <CompanyMenu item={item} closeMenus={closeMenus} />;
  }
}

function MobileChildren({ item, closeMenus }: { item: ManagedHeaderItem; closeMenus: () => void }) {
  const tone = getMenuTone(item.cssClass);

  if (item.cssClass === "services") {
    return (
      <div className="space-y-4 pl-1">
        {item.children.map((child) => {
          return (
            <div key={child.id} className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-900/70">
              <MenuLink href={child.url} target={child.target} onClick={closeMenus} className="mb-3 flex items-center gap-3">
                <div className={cn("flex h-10 w-10 items-center justify-center rounded-xl", tone.mobileIconWrapClass)}>
                  <MenuTypeIcon item={child} layoutKey={item.cssClass} className="h-4 w-4" emojiClassName="text-lg leading-none" />
                </div>
                <span className="text-sm font-semibold text-slate-900 dark:text-white">{child.columnLabel || child.title}</span>
              </MenuLink>
              <div className="space-y-1">
                {child.children.map((grandChild) => (
                  <MenuLink key={grandChild.id} href={grandChild.url} target={grandChild.target} onClick={closeMenus} className="block rounded-lg px-2 py-2 text-sm text-slate-600 dark:text-slate-300">
                    {grandChild.title}
                  </MenuLink>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid gap-2 pl-1">
      {item.children.map((child: ManagedHeaderChild, index) => {
        const mobileIconWrapClass = item.cssClass === "products"
          ? getProductsItemIconStyles(index).wrap
          : tone.mobileIconWrapClass;
        const mobileIconShadowClass = item.cssClass === "products"
          ? getProductsItemIconStyles(index).glow
          : tone.panelGlowClass;
        const mobileIconSizeClass = item.cssClass === "products" ? "h-11 w-11 rounded-2xl" : "h-10 w-10 rounded-xl";
        const mobileIconGlyphClass = item.cssClass === "products" ? "h-5 w-5" : "h-4 w-4";
        const mobileIconEmojiClass = item.cssClass === "products" ? "text-xl leading-none" : "text-lg leading-none";
        return (
          <MenuLink
            key={child.id}
            href={child.url}
            target={child.target}
            onClick={closeMenus}
            className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4 dark:bg-slate-900/70"
          >
            <div className={cn("flex items-center justify-center shadow-lg", mobileIconSizeClass, mobileIconWrapClass, mobileIconShadowClass)}>
              <MenuTypeIcon item={child} layoutKey={item.cssClass} className={mobileIconGlyphClass} emojiClassName={mobileIconEmojiClass} />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 dark:text-white">{child.title}</div>
              {child.description ? <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{child.description}</div> : null}
            </div>
          </MenuLink>
        );
      })}
    </div>
  );
}

export default function Header() {
  const nav = useNavigation();
  const managedHeaderMenu = restructureHeaderMenu(nav.headerMenu || []);
  const desktopHeaderMenu = filterMenuItemsForDevice(managedHeaderMenu, "desktop");
  const mobileHeaderMenu = filterMenuItemsForDevice(managedHeaderMenu, "mobile");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    setActiveMenu(null);
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveMenu(null);
        setMobileOpen(false);
        setMobileExpanded(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const closeMenus = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveMenu(null);
    setMobileOpen(false);
    setMobileExpanded(null);
  }, []);

  const handleMenuEnter = useCallback((key: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveMenu(key);
  }, []);

  const handleMenuLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => setActiveMenu(null), 150);
  }, []);

  const toggleDesktopMenu = useCallback((key: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setActiveMenu((current) => (current === key ? null : key));
  }, []);

  return (
    <header
      ref={headerRef}
      className={cn(
        "fixed left-0 right-0 top-0 z-50 transition-all duration-300",
        scrolled ? "border-b border-slate-200/60 bg-white/80 shadow-sm backdrop-blur-xl dark:border-slate-800/70 dark:bg-slate-950/75" : "bg-transparent"
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-8">
        <BrandLogo />

        <nav className="hidden items-center gap-1 lg:flex">
          {desktopHeaderMenu.map((item, index) => (
            <div
              key={getMenuItemKey(item, index, "header-root")}
              className="relative"
              onMouseEnter={() => (item.children.length > 0 ? handleMenuEnter(item.id) : undefined)}
              onMouseLeave={item.children.length > 0 ? handleMenuLeave : undefined}
            >
              {item.children.length > 0 ? (
                <button
                  type="button"
                  onClick={() => toggleDesktopMenu(item.id)}
                  aria-expanded={activeMenu === item.id}
                  aria-haspopup="menu"
                  className={cn(
                    "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium leading-none transition-colors",
                    activeMenu === item.id
                      ? "bg-sky-50 text-sky-700 dark:bg-slate-900 dark:text-sky-300"
                      : "text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-slate-900 dark:hover:text-white"
                  )}
                >
                  <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white shadow-sm", getMenuTone(item.cssClass).iconWrapClass)}>
                    <MenuTypeIcon item={item} layoutKey={item.cssClass} className="h-3.5 w-3.5" emojiClassName="text-base leading-none" />
                  </div>
                  <span className={cn(isTrendingHighlight(item.title) && "nav-trending-text")}>{item.title}</span>
                  <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", activeMenu === item.id && "rotate-180")} />
                </button>
              ) : (
                <MenuLink
                  href={item.url}
                  target={item.target}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium leading-none text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-slate-900 dark:hover:text-white"
                >
                  <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white shadow-sm", getMenuTone(item.cssClass).iconWrapClass)}>
                    <MenuTypeIcon item={item} layoutKey={item.cssClass} className="h-3.5 w-3.5" emojiClassName="text-base leading-none" />
                  </div>
                  <span className={cn(isTrendingHighlight(item.title) && "nav-trending-text")}>{item.title}</span>
                </MenuLink>
              )}

              <AnimatePresence>
                {item.children.length > 0 && activeMenu === item.id ? (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    className={cn(
                      getDropdownPositionClass(item.cssClass, scrolled),
                      getDropdownPanelClass(item.cssClass)
                    )}
                  >
                    <div className="w-full overflow-hidden rounded-[28px] border border-slate-200/60 bg-white/95 p-6 shadow-2xl shadow-slate-900/10 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95">
                      <DropdownContent item={item} closeMenus={closeMenus} />
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <GlobalSearch />
          <ThemeToggle />
          <Link
            href={LOGIN_DASHBOARD_URL}
            className="hidden lg:block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-slate-900 dark:hover:text-white"
          >
            Login
          </Link>
          <Link href={SIGNUP_DASHBOARD_URL} className="hidden lg:block">
            <Button variant="gradient" size="sm">
              Start Free Trial
            </Button>
          </Link>
          <button
            className="rounded-xl p-2 text-slate-700 transition-colors hover:bg-slate-100 lg:hidden dark:text-slate-200 dark:hover:bg-slate-900"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-slate-200 bg-white lg:hidden dark:border-slate-800 dark:bg-slate-950"
          >
            <div className="space-y-2 px-4 py-4">
              {mobileHeaderMenu.map((item, index) => (
                <div key={getMenuItemKey(item, index, "mobile-header-root")} className="rounded-2xl border border-slate-200/80 bg-white p-2 dark:border-slate-800 dark:bg-slate-950/90">
                  {item.children.length > 0 ? (
                    <button
                      className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm font-semibold leading-none text-slate-900 dark:text-white"
                      onClick={() => setMobileExpanded((current) => (current === item.id ? null : item.id))}
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <span className={cn("flex h-9 w-9 items-center justify-center rounded-xl text-white", getMenuTone(item.cssClass).mobileIconWrapClass)}>
                          <MenuTypeIcon item={item} layoutKey={item.cssClass} className="h-4 w-4" emojiClassName="text-lg leading-none" />
                        </span>
                        <span className={cn("truncate", isTrendingHighlight(item.title) && "nav-trending-text")}>{item.title}</span>
                      </span>
                      <ChevronDown className={cn("h-4 w-4 transition-transform", mobileExpanded === item.id && "rotate-180")} />
                    </button>
                  ) : (
                    <MenuLink href={item.url} target={item.target} onClick={closeMenus} className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-semibold leading-none text-slate-900 dark:text-white">
                      <span className="flex min-w-0 items-center gap-3">
                        <span className={cn("flex h-9 w-9 items-center justify-center rounded-xl text-white", getMenuTone(item.cssClass).mobileIconWrapClass)}>
                          <MenuTypeIcon item={item} layoutKey={item.cssClass} className="h-4 w-4" emojiClassName="text-lg leading-none" />
                        </span>
                        <span className={cn("truncate", isTrendingHighlight(item.title) && "nav-trending-text")}>{item.title}</span>
                      </span>
                    </MenuLink>
                  )}

                  <AnimatePresence>
                    {item.children.length > 0 && mobileExpanded === item.id ? (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden px-2 pb-3">
                        <MobileChildren item={item} closeMenus={closeMenus} />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              ))}

              <div className="flex gap-2 pt-2">
                <Link href={LOGIN_DASHBOARD_URL} onClick={closeMenus} className="flex-1">
                  <Button variant="outline" className="w-full">Login</Button>
                </Link>
                <Link href={SIGNUP_DASHBOARD_URL} onClick={closeMenus} className="flex-1">
                  <Button variant="gradient" className="w-full">Start Free</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
